import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types/database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    '[Supabase] Faltan VITE_SUPABASE_URL y/o VITE_SUPABASE_ANON_KEY — el cliente fallará al usarse.'
  );
}

let cachedClient: SupabaseClient<Database> | null = null;

function getClient(): SupabaseClient<Database> {
  if (cachedClient) return cachedClient;
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Supabase no está configurado. Verifica VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY.'
    );
  }
  cachedClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      // Explícitos (coinciden con los defaults) para dejar el comportamiento documentado.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // flowType se deja en el default para no romper los callbacks OAuth existentes
      // (social-callback, google-calendar-callback). Migrar a 'pkce' exige probar esos flujos.
    },
  });
  return cachedClient;
}

export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop) {
    const client = getClient();
    const value = (client as unknown as Record<string | symbol, unknown>)[prop];
    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(client) : value;
  },
});

/**
 * Usuario de la sesión cacheada (lee de storage, SIN llamada de red — a diferencia de
 * getUser()). Úsalo cuando solo necesitas saber si hay usuario. Para validar el rol
 * contra el servidor (admin/broker) usa requireAuth/requireRole en app/lib/auth.
 */
export async function getCachedUser() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user ?? null;
}
